import { useQuery, keepPreviousData } from "@tanstack/react-query";

import { API_ROUTES } from "../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../api/PrivateAdminPanelInstance";

type StoredToken = { AccessToken?: string };

export type StaffActivityLogQueryParams = {
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  event_category?: string;
  action_type?: string;
  source_ip?: string;
};

export type StaffActivityLogActionType = {
  value: string;
  label: string;
};

export type StaffActivityLogSection = {
  value: string;
  label: string;
  action_types: StaffActivityLogActionType[];
};

export type StaffActivityLogAvailableFilters = {
  sections: StaffActivityLogSection[];
};

export type StaffActivityLogResponse = {
  items: Record<string, unknown>[];
  total_count: number;
  page?: number;
  page_size?: number;
  available_filters?: StaffActivityLogAvailableFilters;
};

// Guard against a malformed `available_filters` block — we defensively drop
// entries missing required fields rather than trusting a bad payload.
function normalizeAvailableFilters(
  raw: unknown,
): StaffActivityLogAvailableFilters | undefined {
  if (raw == null || typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;
  const sectionsRaw = obj.sections;
  if (!Array.isArray(sectionsRaw)) return undefined;

  const sections: StaffActivityLogSection[] = [];
  for (const s of sectionsRaw) {
    if (!s || typeof s !== "object") continue;
    const so = s as Record<string, unknown>;
    if (typeof so.value !== "string" || typeof so.label !== "string") continue;
    const rawActionTypes = Array.isArray(so.action_types) ? so.action_types : [];
    const action_types: StaffActivityLogActionType[] = [];
    for (const a of rawActionTypes) {
      if (!a || typeof a !== "object") continue;
      const ao = a as Record<string, unknown>;
      if (typeof ao.value !== "string" || typeof ao.label !== "string") continue;
      action_types.push({ value: ao.value, label: ao.label });
    }
    sections.push({ value: so.value, label: so.label, action_types });
  }
  return { sections };
}

function normalizeActivityPayload(raw: unknown): StaffActivityLogResponse {
  if (raw == null) {
    return { items: [], total_count: 0 };
  }

  if (Array.isArray(raw)) {
    return { items: raw as Record<string, unknown>[], total_count: raw.length };
  }

  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    let items = (obj.items ?? obj.rows ?? obj.data ?? []) as unknown;
    if (!Array.isArray(items)) {
      items = [];
    }
    const list = items as Record<string, unknown>[];
    const total = Number(
      obj.total_count ?? obj.total ?? obj.count ?? list.length,
    );
    return {
      items: list,
      total_count: Number.isFinite(total) ? total : list.length,
      page: obj.page as number | undefined,
      page_size: obj.page_size as number | undefined,
      available_filters: normalizeAvailableFilters(obj.available_filters),
    };
  }

  return { items: [], total_count: 0 };
}

async function fetchStaffActivityLog(
  adminId: string,
  params: StaffActivityLogQueryParams,
): Promise<StaffActivityLogResponse> {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString
    ? JSON.parse(tokenString)
    : null;
  const client = createPrivateAdminPanelInstance(token?.AccessToken);
  const url = API_ROUTES.GET_STAFF_ACTIVITY_LOG.replace("{admin_id}", adminId);

  const query: Record<string, string | number> = {};
  if (params.start_date) query.start_date = params.start_date;
  if (params.end_date) query.end_date = params.end_date;
  if (params.page != null) query.page = params.page;
  if (params.page_size != null) query.page_size = params.page_size;
  if (params.sort_by) query.sort_by = params.sort_by;
  if (params.sort_order) query.sort_order = params.sort_order;
  if (params.event_category) query.event_category = params.event_category;
  if (params.action_type) query.action_type = params.action_type;
  if (params.source_ip) query.source_ip = params.source_ip;

  const res = await client.get(url, { params: query });
  const payload = res?.data?.data ?? res?.data;
  return normalizeActivityPayload(payload);
}

/**
 * Activity log for a staff member (admin-panel-staff GET …/staff/{admin_id}/activity).
 */
export function useGetStaffActivityLog(
  adminId: string | null | undefined,
  params: StaffActivityLogQueryParams = {},
) {
  const {
    start_date = "",
    end_date = "",
    page = 1,
    page_size = 25,
    sort_by = "",
    sort_order,
    event_category = "",
    action_type = "",
    source_ip = "",
  } = params;

  return useQuery({
    queryKey: [
      "GET_STAFF_ACTIVITY_LOG",
      adminId ?? "",
      start_date,
      end_date,
      page,
      page_size,
      sort_by,
      sort_order ?? "",
      event_category,
      action_type,
      source_ip,
    ],
    queryFn: () => fetchStaffActivityLog(adminId!, params),
    enabled: Boolean(adminId),
    staleTime: 1000 * 30,
    placeholderData: keepPreviousData,
  });
}
